import { ID_BELOW_THE_FOLD } from './constants';
import {
  IconAppWindowPlatform,
  IconBattery1,
  IconColorSwatch,
  IconEye,
  IconEyeClosed,
  IconHourglass,
  IconMaximize,
  IconMaximize2,
  IconMinimize,
  IconMinimize2,
  IconPictureInPicture,
} from './icons';
import { ScrollToMarker } from './ScrollToMarker';

<ScrollToMarker id={ID_BELOW_THE_FOLD} />

What is this?
-------------

You can use this web app to keep your device's screen awake.

Click <IconEyeClosed size={12} /> to enable wake lock.
Click <IconEye size={12} /> to disable it again.

The screen will stay awake as long as the tab is open and the device is not
locked. The wake lock will be released if the tab is closed or minimized and the
screen will turn off as usual.

Click <IconColorSwatch fill="#0006" size={12} /> to turn your
screen black (only on certain devices/browsers). This can help reduce power use
or screen burn-in. Note that this does not automatically enable the wake lock so
you can turn off the screen with or without keeping the device awake.

The <IconBattery1 size={12} /> icon will show if you are running on battery
power and the device is not fully charged. Clicking this button will toggle a
battery status overlay which can help you monitor your battery level
(especially from a distance) while keeping the screen awake.

Click <IconPictureInPicture size={12} /> (shown only if supported in your
browser) to open the app in a picture-in-picture window. This will open a small
floating popup that stays on top of other windows. Note that this popup will
automatically close if you close the main window.

Click <IconAppWindowPlatform size={12} /> to open the app in a separate window.
On a desktop device this new window will have minimal browser UI for a more
compact version of the app.

Click <IconHourglass size={12} /> to show options for automatically disabling
the wake lock at a given time (e.g. stop at midnight) or after a certain
duration (e.g. keep awake for 1 hour).

Clicking <IconMaximize2 size={12} /> and <IconMinimize2 size={12} /> will toggle
the content of this window and only show the app controls.

Clicking <IconMaximize size={12} /> and <IconMinimize size={12} /> will toggle
fullscreen mode for this window. This feature may not be available on all
devices.

Why is this useful?
-------------------

This app is useful for keeping your device's screen awake without having to
change system settings or use other apps. It can be particularly helpful for
developers, testers, or anyone who needs to keep their screen on for extended
periods of time.

[Wake.lol][site] requires zero installation and works across all browsers and
devices.

How does it work?
-----------------

[Wake.lol][site] uses the [Screen Wake Lock API][api] to keep the screen awake.
This API allows web applications to request a wake lock that prevents the screen
from dimming or turning off.

This API is supported in most modern browsers. On unsupported browsers it will
fall back to showing an invisible video which tricks the operating system into
thinking a video is playing, thus keeping the screen awake.

When does it not work?
----------------------

While handy this app cannot fully prevent the screen from turning off in all
cases. For example, if the device is locked or if the browser is closed, the
wake lock will be released and the screen will turn off as usual.

If using virtual machines or remote desktops [wake.lol][site] must be running on
the host system to keep the physical screen awake and also on the guest system
to prevent that system from going to sleep.

Features
--------

* **Offline mode**: This app works even when not connected to the internet.
* **Picture-in-picture window**: Open the app in a small floating window that
  stays on top of other windows.
* **Progressive web app**: This app can be installed to the desktop and run in a
  native-like way.
* **Color schemes**: Supports system light mode and dark mode.
* **Custom theme color**: Replace the default green color with any color of your
  choice.

Stuck on an old version?
------------------------

Version 1 of wake.lol did not support auto-updating. You can force an update by
uninstalling and reinstalling the app or clearing your browser cache.

Alternatively you can follow these steps on Chrome, Edge:

1. Open the Developer Tools (usually Cmd+Option+I / Ctrl+Shift+I)
2. Right-click the reload button
3. Select "Empty Cache and Hard Reload" or "Hard Reload"

Who made this?
--------------

[Wake.lol][site] was created by [J Hoeks][skeoh]. The source code is available
[on GitHub][github].

[site]: https://wake.lol/
[api]: https://developer.mozilla.org/en-US/docs/Web/API/Screen_Wake_Lock_API
[skeoh]: https://skeoh.com/?utm_source=wake.lol
[github]: https://github.com/j-/wake.lol
